import { Action, Mutation, VuexModule } from "vuex-module-decorators";

/**
 * Base class that can be extended to create stores to paginate data.
 *
 * @template DocumentType The type of the document that is listed in the Pagination
 * @template PaginationParamsType The parameters that are needed to make a pagination request
 */
export abstract class BasePagination<DocumentType, PaginationParamsType> extends VuexModule {
  protected abstract _paginationList: DocumentType[];
  protected abstract _currentPage: number;
  protected abstract _itemsPerPage: number;
  protected abstract _totalItems: number;
  protected abstract _totalPages: number;
  protected abstract _pageOrder: "descending" | "ascending";
  protected abstract _isLoadAll: boolean;

  @Action
  protected async loadDocuments(query: any): Promise<any> {
    throw new Error(`loadDocuments not implemented by parent class. Cannot process query ${query}`);
  }

  private _loading = false;

  get paginationList(): DocumentType[] {
    return this._paginationList;
  }
  get currentPage(): number {
    return this._currentPage;
  }
  get itemsPerPage(): number {
    return this._itemsPerPage;
  }
  get totalItems(): number {
    return this._totalItems;
  }
  get totalPages(): number {
    return this._totalPages;
  }
  get pageOrder(): "descending" | "ascending" {
    return this._pageOrder;
  }
  get loading() {
    return this._loading;
  }
  get isLoadAll() {
    return this._isLoadAll;
  }
  get isComplete() {
    return this.paginationList.length >= this.totalItems;
  }

  @Mutation
  private _mutatePaginationList(paginationList: DocumentType[]) {
    this._paginationList = paginationList;
  }
  @Mutation
  private _mutateCurrentPage(currentPage: number) {
    this._currentPage = currentPage;
  }
  @Mutation
  private _mutateItemsPerPage(itemsPerPage: number) {
    this._itemsPerPage = itemsPerPage;
  }
  @Mutation
  private _mutateTotalItems(totalItems: number) {
    this._totalItems = totalItems;
  }
  @Mutation
  private _mutateTotalPages(totalPages: number) {
    this._totalPages = totalPages;
  }
  @Mutation
  private _mutatePageOrder(pageOrder: "descending" | "ascending") {
    this._pageOrder = pageOrder;
  }
  @Mutation
  private _mutateLoading(loading: boolean) {
    this._loading = loading;
  }
  @Mutation
  private _mutateIsLoadAll(isLoadAll: boolean) {
    this._isLoadAll = isLoadAll;
  }

  @Action
  setPaginationList(paginationList: DocumentType[]): void {
    this.context.commit("_mutatePaginationList", paginationList);
  }
  @Action
  setCurrentPage(currentPage: number): void {
    this.context.commit("_mutateCurrentPage", currentPage);
  }
  @Action
  setItemsPerPage(itemsPerPage?: number) {
    this.context.commit("_mutateItemsPerPage", itemsPerPage);
  }
  @Action
  setTotalItems(totalItems: number): void {
    this.context.commit("_mutateTotalItems", totalItems);
  }
  @Action
  setTotalPages(totalPages: number): void {
    this.context.commit("_mutateTotalPages", totalPages);
  }
  @Action
  setPageOrder(pageOrder: "descending" | "ascending") {
    this.context.commit("_mutatePageOrder", pageOrder);
  }
  @Action
  setLoading(loading: boolean) {
    this.context.commit("_mutateLoading", loading);
  }
  @Action
  setIsLoadAll(isLoadAll: boolean) {
    this.context.commit("_mutateIsLoadAll", isLoadAll);
  }
  @Action
  replaceInList(document: DocumentType) {
    const paginationList = this._paginationList;

    const index = this._paginationList.findIndex((el: any) => {
      if (el.id) {
        return el.id === (document as any).id;
      } else {
        return (el as any)._id === (document as any)._id;
      }
    });

    if (index >= 0) {
      paginationList.splice(index, 1, document);
      this.context.commit("_mutatePaginationList", paginationList);
    }
  }

  /**
   * Loads all documents starting at the startId in small batches from the server.
   *
   * @param query the query parameters to send to the server. the current Page param can not be used in this case
   */
  @Action
  async fetchAllPagesByLastId(query: Omit<PaginationParamsType, "currentPage">) {
    const lastDocument = this.paginationList[this.paginationList.length - 1] as any;
    const startId = lastDocument._id || lastDocument.id;
    const documents = await this.context.dispatch("loadDocuments", {
      itemsPerPage: this.itemsPerPage,
      startId: startId,
      ...query
    });

    // set meta information
    this.context.dispatch("setItemsPerPage", documents.meta.itemsPerPage);
    this.context.dispatch("setTotalItems", documents.meta.totalItems);
    this.context.dispatch("setTotalPages", documents.meta.totalPages);

    // replace in list
    const lastKnownElementIndex = this._paginationList.findIndex(el => {
      const id = (el as any)._id || (el as any).id;

      return id === startId;
    });
    const paginationList = this.paginationList;
    paginationList.splice(lastKnownElementIndex + 1, documents.data?.length || 0, ...(documents.data || []));
    this.context.dispatch("setPaginationList", paginationList);

    // load more documents
    if (documents.data.length === this.itemsPerPage && this.isLoadAll) {
      this.context.dispatch("fetchAllPagesByLastId", query);
    }
  }

  /**
   * Loads all documents starting at the page specified in the query in small batches from the server.
   *
   * @param query the query parameters to send to the server. the query parameter startid can not be used in this case
   */
  @Action
  async fetchAllByPageNumber(query: Omit<PaginationParamsType, "startId">) {
    const documents = await this.context.dispatch("loadDocuments", {
      currentPage: this.currentPage,
      itemsPerPage: this.itemsPerPage,
      ...query
    });

    // set meta information
    this.context.dispatch("setCurrentPage", documents.meta.currentPage);
    this.context.dispatch("setItemsPerPage", documents.meta.itemsPerPage);
    this.context.dispatch("setTotalItems", documents.meta.totalItems);
    this.context.dispatch("setTotalPages", documents.meta.totalPages);

    // add to list
    const paginationList = this.paginationList;
    paginationList.splice(
      documents.meta.itemsPerPage * documents.meta.currentPage,
      documents.data?.length || 0,
      ...((documents.data as any) || [])
    );
    this.context.dispatch("setPaginationList", paginationList);

    // load more documents
    if (documents.data.length === this.itemsPerPage && this.isLoadAll) {
      this.context.dispatch("fetchAllPagesByLastId", { ...query });
    }
  }

  @Action
  emptyList() {
    this.context.dispatch("setPaginationList", []);
  }
}
